// hooks/useLiveChatSuperpower.js
import { useState, useEffect, useCallback, useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { formatDate } from './FormatDate.js';
import { UserContext } from '../../UserContext'; 
import axiosInstance from '../../axiosInstance';

const API_URL = process.env.REACT_APP_API_URL;
export const useLiveChat = (onPaymentNeeded) => {
  
  const [supportServices, setSupportServices] = useState(false);
  const [chatWindowVisible, setChatWindowVisible] = useState(false);
  const { user} = useContext(UserContext); 
    
  
  // 2. CHECK TOKEN POCKET - looks at your magic token
  const isValidToken = useCallback(() => {
    const token = JSON.parse(localStorage.getItem('chat3CXToken'));
    const today = formatDate();
    return token && token.date === today;
  }, []);
  

  // 3. CREATE TOKEN POCKET - makes a new magic token
  const createToken = useCallback((messageId) => {
    const today = formatDate();
    const token = {
      messageId: `paymentData.messageId${Date.now()}`,
    };
    localStorage.setItem('chat3CXToken', JSON.stringify(token));
    return token;
  }, []);

  // 4. CLEAN OLD TOKEN POCKET - throws away yesterday's token
  const purgeOldToken = useCallback(() => {
    if (supportServices) return;
    const token = JSON.parse(localStorage.getItem('chat3CXToken'));
    const today = formatDate();
    if (token && token.date !== today) {
      localStorage.removeItem('chat3CXToken');
    }
  }, [supportServices]);

  // 5. ASK SERVER - "Does this user have a support plan?"
  useEffect(() => {
    const checkSupportPlan = async () => {
      setChatWindowVisible(true);
      try {
        const response = await axiosInstance.get(`/users/support-services/${user.email}`);
        const data = response.data;
        setSupportServices(data.support);
        
        // If they have a plan, give them a free token!
        if (data.support) {
          const today = formatDate();
          const token = JSON.parse(localStorage.getItem('chat3CXToken'));
          
          if (!token || token.date !== today) {
            createToken();
          }
        }
      } catch (error) {
        console.error('Error fetching support status:', error);
      }
    };

    if (user.email) {
      purgeOldToken();
      checkSupportPlan();
    }
  }, [user.email, purgeOldToken, createToken, setSupportServices]);

  // 7. SPY ON CHAT WINDOW - "Is someone typing a message?"
  useEffect(() => {
    if (supportServices || isValidToken() || !chatWindowVisible) {
      return;
    }

    let lastMessageTime = 0;
    
    const watchForMessages = () => {
      const now = Date.now();
      // Don't trigger too often (wait 4 seconds between triggers)
      if (now - lastMessageTime < 4000) return;
      
      lastMessageTime = now;
      console.log("👀 Saw a message! Waiting 5 seconds...");

      // Wait 5 seconds, then ask for payment
      setTimeout(() => {
        // Create a special ID for this chat session
        const uuid = uuidv4().slice(0, 4);
        const date = formatDate();
        const messageId = `${date}_${user.email}_${uuid}`;
        
        console.log("💰 Time to ask for payment!");
        
        // Tell the parent component we need payment
        onPaymentNeeded({
          amount: 9.99,
          numericAmount: 9.99,
          messageId: messageId
        });
      }, 5000);
    };

    // 👁️ The EYES that watch for new messages
    const messageSpy = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // It's an HTML element
            // Is this a message from the client?
            const isClientMessage =
              node.classList?.contains('msg_bubble_client_s1i7p') ||
              node.querySelector?.('.msg_bubble_client_s1i7p');
            
            if (isClientMessage) {
              console.log("📨 Found a client message!");
              watchForMessages();
            }
          }
        });
      }
    });

    // 🔍 The SEARCHER that finds the chat box to spy on
    const findChatBox = setInterval(() => {
      const chatWidget = document.querySelector('#wp-live-chat-by-3CX');
      const shadowRoot = chatWidget?.shadowRoot;
      if (!shadowRoot) return;

      // Look for the chat container
      const chatContainer = shadowRoot.querySelector('.root_sT5qE');
      const backupTarget = shadowRoot.querySelector('.chatContainer') || shadowRoot;

      if (chatContainer || backupTarget) {
        // Found it! Start spying 👁️
        messageSpy.observe(chatContainer || backupTarget, {
          childList: true,
          subtree: true, // Watch everything inside
        });
        
        console.log("🔍 Now watching for messages...");
        clearInterval(findChatBox); // Stop searching
      }
    }, 1000); // Check every second

    // Clean up when done (stop spying)
    return () => {
      messageSpy.disconnect();
      clearInterval(findChatBox);
    };
  }, [supportServices, isValidToken, chatWindowVisible, user.email, onPaymentNeeded]);

  // 8. The SUPER POWERS we give back
  return {
    supportServices,
    setSupportServices,
    chatWindowVisible,
    setChatWindowVisible,
    isValidToken,
    createToken
  };
};